import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Modal } from "antd";
import dayjs from "dayjs";
import { FC, useEffect } from "react";
import {
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getMotivationInfoThunk,
    postMessengerReceivedDelivery,
} from "tm-shared/api";

import { useAsyncTaskQueue } from "@/hooks/core/useAsyncTaskQueue";
import {
    useAppDispatch,
    useAppSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { ProgressLoader } from "@/shared/feedback";
import BaseButton from "@/shared/ui/BaseButton";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/manager";
import { setShouldUpdateKpi } from "@/store/slices/offline";
import { ModalPayloads, ModalType } from "@/types/modals";
import { getKpiTargetDate } from "@/utils/business/getKpiTargetDate";

import { getKpiProgressCapabilities } from "./KpiProgress.policy";

export interface KpiProgressModalProps {
    payload: ModalPayloads[ModalType.KpiProgress];
    onCloseModal: () => void;
}

const KpiProgressModal: FC<KpiProgressModalProps> = ({
    payload,
    onCloseModal,
}) => {
    const dispatch = useAppDispatch();
    const latency = useAppSelector((s) => s.offline.latency);
    const motivation = useAppSelector((s) => s.work.motivation);
    const employee = useAppSelector((s) => s.user.employee);
    const userRole = useProtectedUserRoleSelector();

    const { queue, progress } = useAsyncTaskQueue();
    const capabilities = getKpiProgressCapabilities(userRole);

    const isOldData =
        motivation?.calculatedAt &&
        dayjs(motivation.calculatedAt).isAfter(dayjs(payload?.calculatedAt));

    useEffect(() => {
        if (payload?.deliveryId) {
            postMessengerReceivedDelivery({
                deliveryId: payload?.deliveryId,
            });
        }
    }, [payload?.deliveryId]);

    useEffect(() => {
        if (isOldData) {
            onCloseModal();
        }
    }, [onCloseModal, isOldData]);

    const handleConfirm = async () => {
        const commonKpiParams = {
            employeeId: employee?.id ?? "",
            targetDate: getKpiTargetDate(),
            roleSlug: userRole,
        };

        queue.add("Завантаження зарплати", () =>
            dispatch(
                capabilities.canUseManagerKpiRequests
                    ? getKpiGetSalaryToPaySvThunk(commonKpiParams)
                    : getKpiGetSalaryToPayThunk(commonKpiParams),
            ).unwrap(),
        );

        queue.add("Завантаження прогресу KPI", () =>
            dispatch(
                capabilities.canUseManagerKpiRequests
                    ? getKpiGetProgressSvThunk(commonKpiParams)
                    : getKpiGetProgressThunk(commonKpiParams),
            ).unwrap(),
        );

        queue.add("Завантаження інформації про мотивацію", () =>
            dispatch(
                getMotivationInfoThunk({
                    employeeId: employee?.id ?? "",
                }),
            ).unwrap(),
        );

        try {
            await queue.runParallel();
        } catch (error) {
            console.error("Init error:", error);
        } finally {
            setTimeout(() => {
                queue.clear();

                onCloseModal();
            }, 2000);
        }
    };

    const handleCancel = () => {
        dispatch(setShouldUpdateKpi(true));
        onCloseModal();
    };

    if (isOldData) {
        return null;
    }

    const isLoaderOpen = progress.isRunning || progress.tasks.length > 0;

    const isStableInternet =
        typeof latency === "string" ? false : latency < 1000;

    return (
        <>
            <Modal open title="" closable={false} centered footer={[]}>
                <Flex vertical gap={16}>
                    <PrimaryText
                        style={{ textAlign: "center" }}
                        fontSize={20}
                        fontWeight="500"
                    >
                        Дані KPI оновлено
                    </PrimaryText>
                    <SecondaryText>
                        Оновити інформацію на дашборді зараз?
                    </SecondaryText>

                    {!isStableInternet && (
                        <Flex gap={8}>
                            <InfoCircleOutlined style={{ fontSize: 20 }} />
                            <SecondaryText>
                                Оновити дані можливо лише за наявності
                                стабільного інтернет з&apos;єднання
                            </SecondaryText>
                        </Flex>
                    )}

                    <Flex vertical gap={8}>
                        <BaseButton
                            type="primary"
                            onClick={handleConfirm}
                            disabled={!isStableInternet}
                        >
                            Оновити зараз
                        </BaseButton>

                        <BaseButton type="default" onClick={handleCancel}>
                            Оновити пізніше
                        </BaseButton>
                    </Flex>
                </Flex>
            </Modal>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};

export default KpiProgressModal;
